import { Trans, useLingui } from '@lingui/react/macro';
import { Typography } from '@remarkable/ark-web';

import type {
  CloudConnectSubscription,
  Price,
} from 'src/api/endpoints/cloudConnect.types';
import { FlexDivider, Sheet, TextWithLabel } from 'src/components';
import { formatDate } from 'src/utils/formatDate';
import { formatAmount } from 'src/utils/productUtils';

import { CardHeading } from './CardHeading';

const formatPrice = (price: Price | null | undefined, locale: string) => {
  if (!price) return null;

  return formatAmount(price.totalAmount, price.currency, locale);
};

export const CloudConnectNoBillingDetails = () => (
  <Typography variant="body-md">
    <Trans>No billing details available</Trans>
  </Typography>
);

export const CloudConnectNextBillingCard = ({
  subscription,
}: {
  subscription: CloudConnectSubscription;
}) => {
  const { t, i18n } = useLingui();
  const nextBillingTitle = t`Next billing`;

  if (subscription.cancelAtPeriodEnd) {
    const cancellationDate = formatDate(
      subscription.cancelAt ?? subscription.periodEnd,
      { locale: i18n.locale }
    );

    return (
      <Sheet className="gap-24">
        <CardHeading>{nextBillingTitle}</CardHeading>
        <Typography variant="body-md">
          <Trans>
            Your subscription has been cancelled and will expire on{' '}
            {cancellationDate}
          </Trans>
        </Typography>
      </Sheet>
    );
  }

  const invoice = subscription.nextInvoice;

  return (
    <Sheet className="gap-24">
      <CardHeading>{nextBillingTitle}</CardHeading>

      {invoice ? (
        <div className="flex w-full flex-col flex-wrap gap-16 lm:flex-row">
          <TextWithLabel label={t`Date`} className="flex-1">
            {formatDate(invoice.nextPaymentAttempt, { locale: i18n.locale })}
          </TextWithLabel>

          <FlexDivider />

          <TextWithLabel label={t`Subscription`} className="flex-1">
            <Trans>Connect</Trans>
          </TextWithLabel>

          <FlexDivider />

          <TextWithLabel label={t`Amount`} className="flex-1">
            {formatPrice(invoice.amountDue, i18n.locale) ?? t`N/A`}
          </TextWithLabel>
        </div>
      ) : (
        <CloudConnectNoBillingDetails />
      )}
    </Sheet>
  );
};
